import React from 'react'
import {
  Grid,
  GridCell,
  Flex,
  FlexItem,
  InputNumber,
  FormElement,
  SpacingVertical,
  Divider
} from 'mailup-web-components'
import { useIntl } from 'react-intl'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  defaultStyleState,
  pageState,
  styleState
} from 'Pages/EmailContentDefaults/recoil/state/state'
import { ParagraphRowHeader } from './ParagraphRowHeader'
import FontFamilySelect from 'Pages/EmailContentDefaults/shared/FontFamilySelect'
import { isNumber } from 'Pages/EmailContentDefaults/shared/utils'
import AlignSelect from 'Pages/EmailContentDefaults/shared/AlignSelect'

import iconTypography from '../../icons/cd-icon-typography.svg'
import styled from '@emotion/styled'

const StyleGrid = styled(Grid)`
  margin: 0 12px;
`

export const ParagraphTypography = () => {
  const intl = useIntl()
  const setPageState = useSetRecoilState(pageState)
  const [
    {
      paragraph: { styles }
    },
    setContentDefaults
  ] = useRecoilState(styleState)

  const {
    paragraph: { styles: defaultStyles }
  } = useRecoilValue(defaultStyleState)
  const { isEnabled } = useRecoilValue(pageState)

  const onChangeFontSize = e => {
    setPageState(prev => ({
      ...prev,
      isDirty: true
    }))

    const fontSize = e ? e + 'px' : defaultStyles.fontSize

    setContentDefaults(prev => ({
      ...prev,
      paragraph: {
        ...prev.paragraph,
        styles: {
          ...prev.paragraph.styles,
          fontSize
        }
      }
    }))
  }

  const onChangeLineHeight = e => {
    setPageState(prev => ({
      ...prev,
      isDirty: true
    }))
    const lineHeight = e ? e + '%' : defaultStyles.lineHeight
    setContentDefaults(prev => ({
      ...prev,
      paragraph: {
        ...prev.paragraph,
        styles: {
          ...prev.paragraph.styles,
          lineHeight
        }
      }
    }))
  }

  const onChangeFontFamily = e => {
    setPageState(prev => ({
      ...prev,
      isDirty: true
    }))
    setContentDefaults(prev => ({
      ...prev,
      paragraph: {
        ...prev.paragraph,
        styles: {
          ...prev.paragraph.styles,
          fontFamily: e || defaultStyles.fontFamily
        }
      }
    }))
  }

  const onChangeTextAlign = e => {
    setPageState(prev => ({
      ...prev,
      isDirty: true
    }))
    setContentDefaults(prev => ({
      ...prev,
      paragraph: {
        ...prev.paragraph,
        styles: {
          ...prev.paragraph.styles,
          textAlign: e || defaultStyles.textAlign
        }
      }
    }))
  }

  const onChangeParagraphSpacing = e => {
    setPageState(prev => ({
      ...prev,
      isDirty: true
    }))
    setContentDefaults(prev => ({
      ...prev,
      paragraph: {
        ...prev.paragraph,
        styles: {
          ...prev.paragraph.styles,
          paragraphSpacing: isNumber(e)
            ? e + 'px'
            : defaultStyles.paragraphSpacing
        }
      }
    }))
  }

  const onChangeLetterSpacing = e => {
    setPageState(prev => ({
      ...prev,
      isDirty: true
    }))
    setContentDefaults(prev => ({
      ...prev,
      paragraph: {
        ...prev.paragraph,
        styles: {
          ...prev.paragraph.styles,
          letterSpacing: isNumber(e) ? e + 'px' : defaultStyles.letterSpacing
        }
      }
    }))
  }

  return (
    <>
      <ParagraphRowHeader
        title={'Load.ContentDefaults.CommonTypographyTitle'}
        icon={iconTypography}
      />
      <StyleGrid cols={12}>
        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.ContentDefaults.TypographyFontFamilyLabel'
            })}
          >
            <FontFamilySelect
              value={styles.fontFamily}
              onChange={onChangeFontFamily}
              disabled={!isEnabled}
            />
          </FormElement>
        </GridCell>
        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.ContentDefaults.TypographyFontSizeLabel'
            })}
          >
            <Flex alignItems={'center'}>
              <FlexItem>
                <InputNumber
                  min={1}
                  value={parseInt(styles.fontSize.replace('px', ''))}
                  onChange={onChangeFontSize}
                  disabled={!isEnabled}
                />
              </FlexItem>
              <FlexItem margin='0 0 0 8px'>px</FlexItem>
            </Flex>
          </FormElement>
        </GridCell>
        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.ContentDefaults.TypographyLineHeightLabel'
            })}
          >
            <Flex alignItems={'center'}>
              <FlexItem>
                <InputNumber
                  min={1}
                  value={parseInt(styles.lineHeight.replace('%', ''))}
                  onChange={onChangeLineHeight}
                  disabled={!isEnabled}
                />
              </FlexItem>
              <FlexItem margin='0 0 0 8px'>%</FlexItem>
            </Flex>
          </FormElement>
        </GridCell>

        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.ContentDefaults.TypographyAlignLabel'
            })}
          >
            <AlignSelect
              value={styles.textAlign}
              onChange={onChangeTextAlign}
              disabled={!isEnabled}
            />
          </FormElement>
        </GridCell>
        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.ContentDefaults.TypographyLetterSpacingLabel'
            })}
          >
            <Flex alignItems={'center'}>
              <FlexItem>
                <InputNumber
                  min={-99}
                  max={99}
                  value={parseInt(styles.letterSpacing.replace('px', ''))}
                  onChange={onChangeLetterSpacing}
                  disabled={!isEnabled}
                />
              </FlexItem>
              <FlexItem margin='0 0 0 8px'>px</FlexItem>
            </Flex>
          </FormElement>
        </GridCell>
        <GridCell span={4}>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.ContentDefaults.TypographyParagraphSpacingLabel'
            })}
          >
            <Flex alignItems={'center'}>
              <FlexItem>
                <InputNumber
                  min={0}
                  max={99}
                  value={parseInt(styles.paragraphSpacing.replace('px', ''))}
                  onChange={onChangeParagraphSpacing}
                  disabled={!isEnabled}
                />
              </FlexItem>
              <FlexItem margin='0 0 0 8px'>px</FlexItem>
            </Flex>
          </FormElement>
        </GridCell>
      </StyleGrid>
      <SpacingVertical />
      <Divider />
      <SpacingVertical />
    </>
  )
}
